/**
 * Tela de conflitos SoD — /configuracoes/acessos/conflitos-sod.
 *
 * Lista pares conflitantes em cards (sao poucos, ~5-20), com lista de
 * usuarios em violacao expandida para cada par. CRUD em slide-over.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiPlus, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import {
  criarNomePermissao,
  indexarPorCodigo,
  rotuloPermissao,
} from "../../../components/PermissionMatrix/arvore.js";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";

import {
  criarConflitoSod,
  deletarConflitoSod,
  fetchCatalogoPermissoes,
  fetchConflitosSodViolacoes,
  patchConflitoSod,
} from "../api";
import {
  ActionsBar,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  DangerButton,
  EmptyMsg,
  ErrorMsg,
  Field,
  InfoMsg,
  InlineForm,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Select,
  Textarea,
} from "../styles.jsx";

function SodPagina() {
  const navigate = useNavigate();
  const { request, permissoes } = useAuth();
  // As regras valem para TODAS as marcenarias: so a Pharu edita
  // (`permissao:manage_conflitos` e de plataforma). Confere a lista
  // efetiva crua, sem o atalho do Master — o Master de uma marcenaria nao
  // tem essa permissao.
  const podeEditarRegras = (permissoes?.efetivas || []).includes("permissao:manage_conflitos");
  const [conflitos, setConflitos] = useState([]);
  const [catalogoPlano, setCatalogoPlano] = useState([]);
  const [nomePermissao, setNomePermissao] = useState(() => (codigo) => codigo);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [criarOpen, setCriarOpen] = useState(false);
  const [novo, setNovo] = useState({
    permissao_a: "",
    permissao_b: "",
    motivo: "",
    severidade: "bloqueio",
  });

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [violacoes, catalogo] = await Promise.all([
        fetchConflitosSodViolacoes(request),
        fetchCatalogoPermissoes(request, true),
      ]);
      setConflitos(violacoes?.resultados || []);
      // Lista plana para os selects: o nome legivel da permissao (o mesmo da
      // matriz de acessos) com o modulo/pagina de onde ela e — o codigo
      // tecnico sozinho nao dizia ao gestor o que estava escolhendo.
      const indice = indexarPorCodigo(catalogo?.modulos || []);
      setNomePermissao(() => criarNomePermissao(catalogo?.modulos || []));
      setCatalogoPlano(
        Object.entries(indice)
          .map(([codigo, item]) => ({
            codigo,
            texto: `${item.modulo} › ${item.pagina} — ${rotuloPermissao(item.permissao)}`,
          }))
          .sort((a, b) => a.texto.localeCompare(b.texto, "pt-BR"))
      );
    } catch (err) {
      setError(err?.message || "Erro ao carregar conflitos SoD.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleCriar = async (e) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    try {
      await criarConflitoSod(request, novo);
      setMsg("Conflito criado.");
      setCriarOpen(false);
      setNovo({ permissao_a: "", permissao_b: "", motivo: "", severidade: "bloqueio" });
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao criar conflito.");
    }
  };

  const handleToggleAtivo = async (conflito) => {
    try {
      await patchConflitoSod(request, conflito.id, { ativo: !conflito.ativo });
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao atualizar conflito.");
    }
  };

  const handleExcluir = async (conflito) => {
    if (!window.confirm(`Excluir o conflito "${nomePermissao(conflito.permissao_a)}" x "${nomePermissao(conflito.permissao_b)}"?`)) return;
    try {
      await deletarConflitoSod(request, conflito.id);
      setMsg("Conflito excluído.");
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao excluir conflito.");
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Conflitos de segregação de funções (SoD)</CardTitle>
            <CardSubtitle>
              Pares de permissões que não devem coexistir no mesmo usuário.
              "Bloqueio" recusa a atribuição; "Aviso" pede justificativa.
            </CardSubtitle>
          </div>
          <ActionsBar>
            {podeEditarRegras && (
              <PrimaryButton type="button" onClick={() => setCriarOpen(true)}>
                <FiPlus size={14} /> Novo par
              </PrimaryButton>
            )}
            <SecondaryButton type="button" onClick={carregar} disabled={loading}>
              <FiRefreshCw size={14} /> Atualizar
            </SecondaryButton>
          </ActionsBar>
        </CardHeader>

        {msg && <InfoMsg>{msg}</InfoMsg>}
        {error && <ErrorMsg>{error}</ErrorMsg>}

        {loading ? (
          <EmptyMsg>Carregando...</EmptyMsg>
        ) : !conflitos.length ? (
          <EmptyMsg>
            Nenhum par cadastrado. Adicione conflitos para ajudar a separar
            funções críticas (ex.: criar ordem + aprovar ordem).
          </EmptyMsg>
        ) : (
          conflitos.map((c) => (
            <Card
              key={c.id}
              style={{
                borderColor: c.total_violacoes > 0 ? "currentColor" : undefined,
              }}
            >
              <CardHeader>
                <div>
                  <CardTitle>
                    <span title={c.permissao_a}>{nomePermissao(c.permissao_a)}</span>{" "}
                    <span aria-label="conflita com">⚡</span>{" "}
                    <span title={c.permissao_b}>{nomePermissao(c.permissao_b)}</span>
                  </CardTitle>
                  <CardSubtitle>{c.motivo}</CardSubtitle>
                </div>
                <ActionsBar>
                  <Pill $tone={c.severidade === "bloqueio" ? "danger" : "warning"}>
                    {c.severidade}
                  </Pill>
                  <Pill $tone={c.ativo ? "success" : "neutral"}>
                    {c.ativo ? "Ativo" : "Inativo"}
                  </Pill>
                  {c.total_violacoes > 0 && (
                    <Pill $tone="danger">
                      <FiAlertTriangle size={11} /> {c.total_violacoes} em violação
                    </Pill>
                  )}
                </ActionsBar>
              </CardHeader>

              {c.usuarios_em_violacao?.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {c.usuarios_em_violacao.map((u) => (
                    <li key={u.usuario_id}>
                      <button
                        type="button"
                        style={{
                          background: "transparent",
                          border: 0,
                          color: "inherit",
                          textDecoration: "underline",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        onClick={() => navigate(`/configuracoes/acessos/usuarios/${u.usuario_id}`)}
                      >
                        {u.nome}
                      </button>
                      {" — "}
                      <span style={{ opacity: 0.75 }}>{u.cargo}</span>
                    </li>
                  ))}
                </ul>
              )}

              {podeEditarRegras && (
                <ActionsBar>
                  <SecondaryButton type="button" onClick={() => handleToggleAtivo(c)}>
                    {c.ativo ? "Desativar" : "Reativar"}
                  </SecondaryButton>
                  <DangerButton type="button" onClick={() => handleExcluir(c)}>
                    <FiTrash2 size={14} /> Excluir
                  </DangerButton>
                </ActionsBar>
              )}
            </Card>
          ))
        )}
      </Card>

      <SlideOverPanel
        open={criarOpen}
        onClose={() => setCriarOpen(false)}
        title="Novo par de conflito SoD"
        subtitle="Defina duas permissões que não devem coexistir."
        width="min(95vw, 520px)"
        footer={
          <>
            <SecondaryButton type="button" onClick={() => setCriarOpen(false)}>
              Cancelar
            </SecondaryButton>
            <PrimaryButton type="submit" form="form-novo-sod">
              <FiPlus size={14} /> Criar
            </PrimaryButton>
          </>
        }
      >
        <InlineForm id="form-novo-sod" onSubmit={handleCriar}>
          <Field>
            Permissão A
            <Select
              required
              value={novo.permissao_a}
              onChange={(e) => setNovo((p) => ({ ...p, permissao_a: e.target.value }))}
            >
              <option value="">Selecione...</option>
              {catalogoPlano.map((c) => (
                <option key={`a-${c.codigo}`} value={c.codigo}>
                  {c.texto}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Permissão B
            <Select
              required
              value={novo.permissao_b}
              onChange={(e) => setNovo((p) => ({ ...p, permissao_b: e.target.value }))}
            >
              <option value="">Selecione...</option>
              {catalogoPlano.map((c) => (
                <option key={`b-${c.codigo}`} value={c.codigo}>
                  {c.texto}
                </option>
              ))}
            </Select>
          </Field>
          <Field className="full">
            Motivo
            <Textarea
              required
              value={novo.motivo}
              onChange={(e) => setNovo((p) => ({ ...p, motivo: e.target.value }))}
              placeholder="Explique por que essas permissões não devem coexistir."
            />
          </Field>
          <Field>
            Severidade
            <Select
              value={novo.severidade}
              onChange={(e) => setNovo((p) => ({ ...p, severidade: e.target.value }))}
            >
              <option value="bloqueio">Bloqueio (recusa)</option>
              <option value="aviso">Aviso (pede justificativa)</option>
            </Select>
          </Field>
        </InlineForm>
      </SlideOverPanel>
    </>
  );
}

export default SodPagina;
